import React, { useState, useEffect } from 'react'
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import { Tooltip } from 'react-tooltip'
import moment from 'moment-timezone'

function LoadDetails({ load, isDarkMode, styles, onFacilityClick }) {
	const [isOpen, setIsOpen] = useState({})

	// Helper functions
	const calculateTimeDifference = (puTime, delTime) => {
		const startTime = new Date(puTime)
		const endTime = new Date(delTime)
		let diff = endTime - startTime

		const days = Math.floor(diff / (24 * 60 * 60 * 1000))
		diff -= days * (24 * 60 * 60 * 1000)
		const hours = Math.floor(diff / (60 * 60 * 1000))
		diff -= hours * (60 * 60 * 1000)
		const minutes = Math.floor(diff / (60 * 1000))

		const timeParts = []
		if (days > 0) timeParts.push(days + 'd')
		if (hours > 0 || days > 0) timeParts.push(hours + 'h')
		if (minutes > 0) timeParts.push(minutes + 'm')

		return timeParts.join(' ')
	}

	const formatWeight = weight => {
		const pounds = Number(weight)
		if (!Number.isFinite(pounds) || pounds < 11000) {
			return ' '
		}
		return (pounds / 1000).toFixed(0) + 'K lbs'
	}

	const truncateToLastSix = input => {
		const str = input.toString()
		if (str.length <= 6) {
			return '...' + str
		}
		return '...' + str.substring(str.length - 6)
	}

	const capitalName = str => {
		return str
			?.toLowerCase()
			?.split(' ')
			?.map(word => word.charAt(0).toUpperCase() + word.slice(1))
			?.join(' ')
	}

	const truncateLabel = label => {
		if (!label || label.length <= 4) return label
		return label.substring(0, 4) + '...'
	}

	const formatDateTime = (dateTime, timezone) => {
		if (!dateTime) return ''

		if (timezone) {
			return moment(dateTime).tz(timezone).format('ddd MMM DD HH:mm z')
		}
		return moment(dateTime).format('ddd MMM DD HH:mm z')
	}

	const getTrailerDetails = loadItem => {
		const trailerDetails = loadItem?.equipmentType

		if (!trailerDetails) return null

		if (
			trailerDetails === 'FIFTY_THREE_FOOT_TRUCK' ||
			trailerDetails === 'FIFTY_THREE_FOOT_A5_AIR_TRAILER'
		) {
			return "53' Trailer"
		} else if (trailerDetails === 'FIFTY_THREE_FOOT_CONTAINER') {
			return "53' Container and Chassis"
		} else if (trailerDetails === 'FORTY_FOOT_CONTAINER') {
			return "40' Container and Chassis"
		} else if (trailerDetails === 'TWENTY_SIX_FOOT_BOX_TRUCK') {
			return "26' Truck"
		} else {
			const trailerWords = trailerDetails.split('_')
			const formattedTrailerDetails = trailerWords
				.map(word => {
					if (word === 'FOOT') {
						return "'"
					} else {
						return word?.charAt(0) + word?.slice(1)?.toLowerCase()
					}
				})
				?.join(' ')

			// Replace "Fifty Three" with "53" at the beginning
			if (formattedTrailerDetails.startsWith('Fifty Three')) {
				return formattedTrailerDetails.replace('Fifty Three', '53')
			}

			return formattedTrailerDetails
		}
	}

	const toggleOpen = stopId => {
		setIsOpen(prevState => ({
			...prevState,
			[stopId]: !prevState[stopId],
		}))
	}

	// Auto-expand if less than 2 loads
	useEffect(() => {
		if (load?.stops?.length < 2) {
			setIsOpen(prevState => ({
				...prevState,
				[`${load?._id}-0`]: true,
				[`${load?._id}-1`]: true,
			}))
		}
	}, [load])

	if (!load?.stops || load.stops.length === 0) {
		return (
			<div className="py-3 text-center text-gray-500">
				No detailed stops information available
			</div>
		)
	}

	const loadIndicator = (loadType, stopIndex, stopGroupIndex) => {
		const tooltipId = `load-indicator-${stopGroupIndex}-${stopIndex}`

		if (loadType === 'LOADED') {
			return (
				<span
					className={`inline-block w-2.5 h-2.5 rounded-full mr-0.5 ${
						isDarkMode ? 'bg-white' : 'bg-black'
					}`}
					data-tooltip-id={tooltipId}
					data-tooltip-content="Loaded"
				/>
			)
		} else if (loadType === 'EMPTY') {
			return (
				<span
					className={`inline-block w-2.5 h-2.5 rounded-full mr-0.5 ${
						isDarkMode
							? 'bg-gray-800 border border-white'
							: 'bg-white border border-black'
					}`}
					data-tooltip-id={tooltipId}
					data-tooltip-content="Empty"
				/>
			)
		} else if (loadType === 'BOBTAIL') {
			return <span>Bobtail</span>
		} else {
			return null
		}
	}

	return (
		<div className="py-1">
			<div className="space-y-px">
				{load.stops.map((stopGroup, index) => {
					const stopId = `${load._id}-${index}`
					const stopIDTruncated = truncateToLastSix(stopId)
					const isExpanded = isOpen[stopId]

					const labelPU = stopGroup.stops[0]?.location?.label
					const labelDel =
						stopGroup.stops[stopGroup.stops.length - 1]?.location?.label

					const distance = stopGroup?.distance?.value?.toFixed(1)
					const payout = stopGroup?.payout?.value?.toFixed(2)

					const puTime = stopGroup?.stops[0]?.actions[0]?.plannedTime
					const delTime =
						stopGroup?.stops[stopGroup?.stops?.length - 1]?.actions[0]
							?.plannedTime

					const timeDiff = calculateTimeDifference(puTime, delTime)

					const isLiveStop = stopGroup?.stops?.find(
						s => s?.unloadingType === 'LIVE' || s?.loadingType === 'LIVE'
					)
					const isLive = isLiveStop ? 'Live' : 'Drop'

					const weightValue = formatWeight(stopGroup?.stops[0]?.weight?.value)

					const isBobtail = stopGroup?.loadType === 'BOBTAIL'
					const loadType = stopGroup?.loadType === 'EMPTY' ? 'Empty' : 'Loaded'

					return (
						<div
							key={stopId}
							className="border rounded overflow-hidden"
							style={{
								borderColor: styles.borderColor,
								backgroundColor: isDarkMode ? '#2d333b' : '#ffffff',
							}}
						>
							{/* Header */}
							<div
								className={`p-1 cursor-pointer transition-colors ${
									isExpanded ? 'border-b' : ''
								}`}
								style={{
									borderColor: styles.borderColor,
									backgroundColor: isDarkMode ? '#363e47' : '#f7f7f9',
								}}
								onClick={() => toggleOpen(stopId)}
							>
								<div className="flex items-center gap-2 md:gap-0 md:justify-between text-[11px] md:text-xs w-full">
									{/* Expand/Collapse Icon and ID */}
									<div className="flex items-center shrink-0 md:flex-1">
										{isExpanded ? (
											<ChevronDownIcon className="w-3 h-3 text-gray-600 md:mr-1" />
										) : (
											<ChevronRightIcon className="w-3 h-3 text-gray-600 md:mr-1" />
										)}
										<button
											className="text-left p-0 text-blue-600 hover:text-blue-800 hidden md:block"
											style={{ color: '#077398' }}
										>
											{stopIDTruncated}
										</button>
									</div>

									{/* Route */}
									<div className="flex items-center shrink-0 -ml-2 md:ml-0 md:flex-1">
										<span className="badge badge-accent w-3.5 h-3.5 leading-[14px] md:w-4 md:h-4 md:leading-4 text-center mr-1 text-[9px] md:text-[10px] p-0 min-h-0">
											{stopGroup?.stops[0]?.stopSequenceNumber}
										</span>
										<span
											className={`text-[9px] m-0 leading-tight mr-1 md:mr-2 ${
												isDarkMode ? 'text-gray-300' : 'text-black'
											}`}
										>
											{truncateLabel(labelPU)}
										</span>

										<span className="mr-1 md:mr-2">→</span>

										<span className="badge badge-accent w-3.5 h-3.5 leading-[14px] md:w-4 md:h-4 md:leading-4 text-center mr-1 text-[9px] md:text-[10px] p-0 min-h-0">
											{
												stopGroup?.stops[stopGroup?.stops?.length - 1]
													?.stopSequenceNumber
											}
										</span>
										<span
											className={`text-[9px] m-0 leading-tight ${
												isDarkMode ? 'text-gray-300' : 'text-black'
											}`}
										>
											{truncateLabel(labelDel)}
										</span>
									</div>

									{/* Distance/Time */}
									<div className="flex flex-col shrink-0 md:flex-1">
										<span className="text-[10px]">{distance} mi</span>
										<span className="text-gray-500 text-[9px]">{timeDiff}</span>
									</div>

									{/* Live/Weight */}
									<div className="shrink-0 md:flex-1 ml-1 md:ml-0">
										<span>{isLive}</span>
										<span>{` ${weightValue}`}</span>
										{isBobtail && (
											<span className="text-[10px] text-red-500 ml-1 italic font-bold">
												Bobtail
											</span>
										)}
									</div>

									{/* Load Type */}
									<div className="shrink-0 w-9 text-center md:w-auto md:text-left md:flex-1">
										{loadType}
									</div>

									{/* Payout */}
									<div className="shrink-0 ml-auto md:ml-0 md:flex-1">${payout}</div>
								</div>
							</div>

							{/* Collapsible Content */}
							{isExpanded && (
								<div className="px-3 pb-2">
									<div className="overflow-x-auto">
										<table className="w-full text-xs">
											<thead>
												<tr
													className="border-b"
													style={{
														borderColor: isDarkMode
															? 'rgba(255,255,255,0.06)'
															: '#eef0f3',
													}}
												>
													<th className="text-left py-2 px-3 text-[10px] font-bold w-1/3">
														Stop
													</th>
													<th className="text-left py-2 px-3 text-[10px] font-bold w-1/6">
														Equipment/Id
													</th>
													<th className="text-left py-2 px-3 text-[10px] font-bold w-1/4">
														Arrival
													</th>
													<th className="text-left py-2 px-3 text-[10px] font-bold w-1/4">
														Departure
													</th>
												</tr>
											</thead>
											<tbody>
												{stopGroup.stops.map((stop, stopIndex) => {
													const pickLabel = stop?.location?.label
													const pickStreet = stop?.location?.line1
													const pickupCity = stop?.location?.city
													const pickupState = stop?.location?.state
													const stopTimezone = stop?.location?.timeZone

													const pickDate = stop?.actions[0]?.plannedTime
													const pickupDate = formatDateTime(pickDate, stopTimezone)

													const pickDep = stop?.actions[1]?.plannedTime
													const pickDepDate = formatDateTime(pickDep, stopTimezone)

													const isLiveStop =
														stop?.unloadingType === 'LIVE' ||
														stop?.loadingType === 'LIVE'
													const isLive = isLiveStop ? 'Live' : null

													return (
														<tr
															key={stopIndex}
															className="border-b last:border-b-0"
															style={{
																borderColor: isDarkMode
																	? 'rgba(255,255,255,0.06)'
																	: '#eef0f3',
															}}
														>
															<td className="py-2 px-2 w-1/3">
																<div>
																	<p className="text-xs m-0 leading-tight">
																		<span className="badge badge-accent w-4 h-4 leading-4 text-center mr-1 ml-1 text-[10px] p-0 min-h-0">
																			{stop?.stopSequenceNumber}
																		</span>
																		{pickLabel && (
																			pickLabel.length <= 4 ? (
																				<span
																					className="underline decoration-dotted cursor-pointer"
																					onClick={e => {
																						e.stopPropagation()
																						onFacilityClick?.(pickLabel)
																					}}
																				>
																					{pickLabel}
																				</span>
																			) : (
																				<span>{pickLabel}</span>
																			)
																		)}
																	</p>
																	{pickStreet && (
																		<p className="text-[10px] m-0 leading-tight ml-5">
																			{capitalName(pickStreet)}
																		</p>
																	)}
																	<p className="text-[10px] m-0 leading-tight ml-5">
																		{capitalName(pickupCity)}, {pickupState}
																	</p>
																</div>
															</td>
															<td className="py-1 px-2 text-xs m-0 leading-tight w-1/6 align-top">
																<div className="flex items-center">
																	{loadIndicator(
																		stopGroup.loadType,
																		stopIndex,
																		index
																	)}
																	<span className="ml-1">
																		{!isBobtail && getTrailerDetails(stopGroup)}
																	</span>
																</div>
																{isLive && (
																	<p className="text-[10px] mt-1">{isLive}</p>
																)}
															</td>
															<td className="py-1 px-2 text-[10px] m-0 leading-tight w-1/4 align-top">
																{pickupDate}
															</td>
															<td className="py-1 px-2 text-[10px] m-0 leading-tight w-1/4 align-top">
																{pickDepDate}
															</td>
														</tr>
													)
												})}
											</tbody>
										</table>
									</div>
								</div>
							)}
						</div>
					)
				})}
			</div>

			{/* Tooltips for load indicators */}
			{load.stops.map((stopGroup, index) =>
				stopGroup.stops.map((stop, stopIndex) => (
					<Tooltip
						key={`tooltip-${index}-${stopIndex}`}
						id={`load-indicator-${index}-${stopIndex}`}
						style={{ zIndex: 9999 }}
					/>
				))
			)}
		</div>
	)
}

export default LoadDetails
